import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';

import { ColumnRow } from './column-row';
import style from './edit-columns-modal.module.less';
import { areMaximumColumnsPinned, findLargestGridIndexPinnedId, findLargestGridIndexPinnedName, getColumnDisplayName } from './helpers';
import { messages } from './messages';
import { SimpleModal } from '../../../components/simple-modal';

export const EditColumnsModal = (props) => {
  const { baseColumns, defaultColumnConfigurations, maxPinnableColumns = 2, saveColumnConfiguration, onClose } = props;
  const [columnConfiguration, setColumnConfiguration] = useState(props.columnConfiguration);

  const maximumColumnsPinned = areMaximumColumnsPinned(columnConfiguration, maxPinnableColumns);
  const largestGridIndexPinnedId = findLargestGridIndexPinnedId(columnConfiguration);
  const largestGridIndexPinnedName = findLargestGridIndexPinnedName(columnConfiguration, baseColumns);

  const clearAllColumns = () => setColumnConfiguration([]);
  const onShowColumnChanged = useCallback(
    (columnId) => {
      const newColumnInConfiguration = columnConfiguration.find((col) => col.id === columnId);

      if (!newColumnInConfiguration) {
        const matchingDefaultConfig = defaultColumnConfigurations.find((col) => col.id === columnId);
        setColumnConfiguration([
          ...columnConfiguration,
          {
            ...matchingDefaultConfig,
            index: columnConfiguration.length,
            pinned: false,
          },
        ]);
        return;
      }
      setColumnConfiguration(columnConfiguration.filter((col) => col.id !== columnId));
    },
    [columnConfiguration, defaultColumnConfigurations],
  );

  const onToggleColumnPinned = useCallback(
    (columnId) => {
      const columnInConfigurationIndex = columnConfiguration.findIndex((col) => col.id === columnId);
      if (columnInConfigurationIndex < 0) {
        return;
      }
      const newConfiguration = [...columnConfiguration];
      const columnInConfiguration = columnConfiguration[columnInConfigurationIndex];
      const newPinState = !columnInConfiguration.pinned;
      newConfiguration[columnInConfigurationIndex] = {
        ...columnInConfiguration,
        pinned: newPinState,
      };

      if (maximumColumnsPinned && newPinState) {
        const largestIndexColumnInConfigurationIndex = columnConfiguration.findIndex((col) => col.id === largestGridIndexPinnedId);
        newConfiguration[largestIndexColumnInConfigurationIndex] = {
          ...columnConfiguration[largestIndexColumnInConfigurationIndex],
          pinned: false,
        };
      }

      setColumnConfiguration(newConfiguration);
    },
    [columnConfiguration, largestGridIndexPinnedId, maximumColumnsPinned],
  );

  const saveConfiguration = useCallback(() => {
    saveColumnConfiguration(columnConfiguration);
    onClose();
  }, [onClose, columnConfiguration, saveColumnConfiguration]);

  return (
    <SimpleModal
      actionMessage={messages.save}
      cancelMessage={messages.cancel}
      className={style['edit-columns-modal']}
      headerMessage={messages.header}
      onAction={saveConfiguration}
      onCancel={onClose}
      onClose={onClose}
      size='custom'
    >
      <div className={style['edit-columns-selectors']}>
        <Button className={style['deselect-all']} onClick={clearAllColumns} theme='link'>
          <FormattedMessage {...messages.deselectAll} />
        </Button>
        <Text formattedMessageValues={{ count: columnConfiguration.length }} textType={TextType.Caption} value={messages.selectedCount} />
      </div>
      {baseColumns.map((column) => {
        const configuredColumn = columnConfiguration.find((c) => c.id === column.id);
        const isChecked = !!configuredColumn;
        const isPinned = !!configuredColumn && configuredColumn.pinned;
        return (
          <ColumnRow
            key={column.id}
            columnId={column.id}
            columnName={getColumnDisplayName(column)}
            columnNameThatWillUnpin={maximumColumnsPinned ? largestGridIndexPinnedName : undefined}
            isChecked={isChecked}
            isPinned={isPinned}
            onColumnChecked={onShowColumnChanged}
            onColumnPinToggle={onToggleColumnPinned}
          />
        );
      })}
    </SimpleModal>
  );
};

EditColumnsModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  columnConfiguration: PropTypes.array.isRequired,
  baseColumns: PropTypes.array.isRequired,
  saveColumnConfiguration: PropTypes.func.isRequired,
  defaultColumnConfigurations: PropTypes.array.isRequired,
  maxPinnableColumns: PropTypes.number,
};
